import { useTranslation } from 'react-i18next';
import { useMutation, UseMutationOptions, UseMutationResult } from '@tanstack/react-query';
import {
  ILayout,
  useNotifications,
  configureLayoutQuery,
  DashboardComponentNames,
  IDashboardComponentProperties,
  usePerformanceReportBuilderPageStore,
  getResponseErrorMessage,
} from '@diana/core';
import { PERFORMANCE_REPORT_BUILDER_PAGE_NS } from '../i18n';

type TResult = ILayout<IDashboardComponentProperties, DashboardComponentNames>;

export const useResetDraftLayoutMutation = (
  options?: UseMutationOptions<TResult, unknown, void>,
): UseMutationResult<TResult, unknown, void> => {
  const { t } = useTranslation(PERFORMANCE_REPORT_BUILDER_PAGE_NS);

  const notifications = useNotifications();

  const getLayout = usePerformanceReportBuilderPageStore((s) => s.getLayout);
  const setLayout = usePerformanceReportBuilderPageStore((s) => s.setLayout);

  return useMutation({
    mutationFn: async () => {
      const layout = getLayout();
      if (!layout) throw new Error('layout not found');

      return await configureLayoutQuery.resetDraft({ layoutId: layout.id }).fn({});
    },
    ...options,
    onSuccess: (data, variables, context) => {
      setLayout(data);
      options?.onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      notifications.error({
        message: t('labels.resetChangesError'),
        description: getResponseErrorMessage(error),
      });
      options?.onError?.(error, variables, context);
    },
  });
};
